import React from 'react';
import GET_ASSETS from 'graphql/getAssets';
import { hasProductCode, getProducts } from 'util/hasProductCode';
import { useProductCollectionSlugs } from './useProductCollectionSlugs';
import useSuspendableQuery from 'hooks/useSuspendableQuery';

export const AssetAccessContext = React.createContext({});

const practitionerTrialCode = '00ftpr0000';
const secondarySourcesTrialCode = '00ftop0000';
const practitionerAllAccessCode = '13plxx0000';
const secondarySourcesAllAccessCode = '12mall0000';

function useAssets() {
  const [overideAssets, setAssets] = React.useState(null);
  const { data: assetsData } = useSuspendableQuery(GET_ASSETS, {
    fetchPolicy: 'network-only',
  });

  const { availableProductSlugs } = useProductCollectionSlugs(
    assetsData?.assets,
    /^13/,
  );

  const assets = React.useMemo(() => overideAssets || assetsData?.assets, [
    overideAssets,
    assetsData?.assets,
  ]);

  return { assets, availableProductSlugs, setAssets };
}

function useProductFlags(assets) {
  return React.useMemo(() => {
    const hasCitatorTrial =
      hasProductCode(assets, practitionerTrialCode) ||
      hasProductCode(assets, secondarySourcesTrialCode);
    const hasPractitionerTrial = hasProductCode(assets, practitionerTrialCode);
    const hasSecondarySourcesTrial =
      hasPractitionerTrial || hasProductCode(assets, secondarySourcesTrialCode);
    const hasPractitionerAllAccess = hasProductCode(
      assets,
      practitionerAllAccessCode,
    );
    const hasSecondarySourcesAllAccess = hasProductCode(
      assets,
      secondarySourcesAllAccessCode,
    );
    const hasPractitionerVisible =
      hasProductCode(assets, /^13/) ||
      hasProductCode(assets, practitionerTrialCode, false);
    const hasOnlawBeta = hasProductCode(assets, 'FEATUREFLAG-ONLAWBETA');
    const hasSecondarySourcesProducts = hasProductCode(assets, /^15.+/);
    const hasOnlawProducts = hasProductCode(assets, /^89/);
    const hasCitatorAccess =
      hasProductCode(assets, '11plcx0000') || hasCitatorTrial;
    const hasCommunityProduct =
      hasProductCode(assets, '01cepi0024') ||
      hasProductCode(assets, '01epix0024') ||
      hasProductCode(assets, '55ppxx0000') ||
      hasProductCode(assets, '01rpic2300') ||
      hasProductCode(assets, '02ucxx0000');
    const hasLocalRulesOfCourt = hasProductCode(assets, '16lrcx0000');

    return {
      hasCitatorAccess,
      hasPractitionerTrial,
      hasSecondarySourcesTrial,
      hasPractitionerAllAccess,
      hasSecondarySourcesAllAccess,
      hasPractitionerVisible,
      hasOnlawBeta,
      hasSecondarySourcesProducts,
      hasOnlawProducts,
      hasCommunityProduct,
      trialProduct:
        getProducts(assets, '00ftpr0000', false)[0] ||
        getProducts(assets, '00ftop0000', false)[0],
      hasPracticeAreas: hasProductCode(assets, /^13.+/),
      hasSecondarySources:
        hasSecondarySourcesProducts || hasSecondarySourcesTrial,
      hasProductCode: regex => hasProductCode(assets, regex),
      hasLocalRulesOfCourt,
    };
  }, [assets]);
}

function AssetsForRoute(props) {
  const { assets, availableProductSlugs, setAssets } = useAssets();

  const hasAccess = React.useCallback(
    productCode => {
      return productCode && hasProductCode(assets, productCode, false);
    },
    [assets],
  );

  const productFlags = useProductFlags(assets);

  return (
    <AssetAccessContext.Provider
      value={{
        assets,
        setAssets,
        hasAccess,
        ...productFlags,
        availableProductSlugs,
      }}
    >
      {props.children}
    </AssetAccessContext.Provider>
  );
}

export default function AssetAccess(props) {
  return <AssetsForRoute>{props.children}</AssetsForRoute>;
}
